import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Card, Button, Table, Space, message, Badge, Dropdown, Tag, Tooltip, Input, App, Typography, Select, DatePicker, Spin, Divider, Tabs } from "antd";
import { EyeOutlined, CopyOutlined, MoreOutlined, ExportOutlined, DownOutlined, RocketOutlined, DownloadOutlined, MailOutlined } from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLoadList, useQuickSearch } from "../../../hooks";
import { useTableSelection } from "../../../hooks/useTableSelection";
import { scheduleService } from "../../../api/enhancedApi";
import { formatDate, formatDateTime } from "../../../utils/dateUtils";
import AircraftTag from "../../../components/AircraftTag";
import ScheduleOfficialFilter from "./components/ScheduleOfficialFilter";
import ScheduleDetailDrawer from "./components/ScheduleDetailDrawer";
import { useAuth } from "../../../hooks/useAuth";
import PermissionGuard from "../../../components/auth/PermissionGuard";
import { ExportButton } from "../../../components/export";
import BatchPublishEmailSendDrawer from "./components/BatchPublishEmailSendDrawer";
import UnifiedEmailSendDrawer from "./components/UnifiedEmailSendDrawer";
import ScheduleActivityDrawer from "../../../components/common/ScheduleActivityDrawer";
import CollapsibleFilterBar, { formatArraySummary } from "../../../components/common/CollapsibleFilterBar";
import { getStatusColor, getStatusText } from "./utils/scheduleUtils";
import AirlineIngestSourceButtons from "../AirlineScheduleIngest/AirlineIngestSourceButtons";
import AirlineIngestOfficialWorkspace from "../AirlineScheduleIngest/AirlineIngestOfficialWorkspace";
import {
    hasIngestSource,
    resolveOfficialPageWorkspace,
    OFFICIAL_PAGE_WORKSPACE_QUERY,
    INGEST_WORKSPACE,
    OFFICIAL_WORKSPACE,
} from "../AirlineScheduleIngest/ingestListPresentation";

const ScheduleOfficialList = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    // 获取权限相关功能
    const { hasPermission, loading: authLoading, user } = useAuth();
    const canIngestRead = hasPermission("airline_schedule_ingest:read");
    const canOfficialRead = hasPermission("schedule_official:read");
    const requestedWorkspace = searchParams.get(OFFICIAL_PAGE_WORKSPACE_QUERY);
    const hasOfficialDeepLink = Boolean(searchParams.get("scheduleId"));
    const workspace = resolveOfficialPageWorkspace({
        canIngestRead,
        canOfficialRead,
        requestedWorkspace,
        hasOfficialDeepLink: hasOfficialDeepLink && canOfficialRead,
    });

    // 状态定义
    const [filterParams, setFilterParams] = useState({
        // 默认查询非草稿状态的计划，明确排除DRAFT状态，并遵循API规范使用逗号分隔字符串
        status: "SUBMITTED,CONFIRMATION_PENDING,AIRLINE_CONFIRMED,PUBLISHED,DEPRECATED",
    });
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20, // 与后端默认值保持一致
    });
    const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
    const [selectedSchedule, setSelectedSchedule] = useState(null);

    // 批量发布邮件发送抽屉状态
    const [batchPublishEmailDrawerVisible, setBatchPublishEmailDrawerVisible] = useState(false);

    // 统一邮件发送抽屉状态
    const [unifiedEmailDrawerVisible, setUnifiedEmailDrawerVisible] = useState(false);
    const [unifiedEmailType, setUnifiedEmailType] = useState("publish");

    // 活动记录抽屉状态
    const [activityDrawerVisible, setActivityDrawerVisible] = useState(false);
    const [selectedScheduleForActivity, setSelectedScheduleForActivity] = useState(null);


    // 使用自定义Hook加载数据
    const { data, isLoading, refetch } = useLoadList(
        "officialSchedules",
        scheduleService.getOfficialScheduleList,
        {
            page: pagination.current,
            page_size: pagination.pageSize,
            ...filterParams,
        },
        { immediate: !authLoading && canOfficialRead },
    );

    // 使用表格选择Hook
    const { selectedRowKeys, selectedRows, tableRef, rowSelection, getRowProps, clearSelection, handleRowSelectionChange, getIndexColumn } = useTableSelection({
        rowKey: "id",
        dataSource: data?.items || [],
        pagination,
    });

    // 数据过滤，确保不显示草稿状态计划（双重保障）
    const filteredData = React.useMemo(() => {
        if (!data?.items) return [];
        return data.items.filter((item) => item.status !== "DRAFT");
    }, [data?.items]);

    // URL 参数解析：根据 scheduleId 自动打开详情抽屉
    // 通过 API 直接获取计划详情，解决分页导致找不到数据的问题
    useEffect(() => {
        const scheduleIdParam = searchParams.get("scheduleId");
        if (!scheduleIdParam) return;
        if (!canOfficialRead) {
            searchParams.delete("scheduleId");
            setSearchParams(searchParams, { replace: true });
            return;
        }

        const fetchAndOpenSchedule = async () => {
            try {
                const scheduleId = parseInt(scheduleIdParam, 10);
                
                // 先尝试从当前页数据中查找
                const targetSchedule = filteredData.find(s => s.id === scheduleId);
                
                if (targetSchedule) {
                    setSelectedSchedule(targetSchedule);
                    setDetailDrawerVisible(true);
                } else {
                    // 如果当前页找不到，通过 API 获取
                    const response = await scheduleService.getScheduleById(scheduleId);
                    if (response?.data?.data || response?.data) {
                        const scheduleData = response.data.data || response.data;
                        setSelectedSchedule(scheduleData);
                        setDetailDrawerVisible(true);
                    }
                }
                
                // 深链只打开现网正式表详情，确认成功不得走这条
                searchParams.delete("scheduleId");
                searchParams.delete(OFFICIAL_PAGE_WORKSPACE_QUERY);
                setSearchParams(searchParams, { replace: true });
            } catch (error) {
                console.error("获取计划详情失败:", error);
                message.error("无法获取计划详情");
                searchParams.delete("scheduleId");
                setSearchParams(searchParams, { replace: true });
            }
        };

        fetchAndOpenSchedule();
    }, [searchParams, setSearchParams, filteredData, canOfficialRead]);

    // 全选当前页面所有计划
    const handleSelectAllVisible = useCallback(() => {
        const visibleKeys = filteredData.map((item) => item.id);
        handleRowSelectionChange(visibleKeys, filteredData);
    }, [filteredData, handleRowSelectionChange]);

    // 自定义行属性函数 - 只允许序号列选中，移除整行点击
    const getCustomRowProps = useCallback(
        (record, index) => {
            const originalProps = getRowProps(record, index);
            // 移除原始的 onClick 事件，只保留其他属性
            const { onClick, ...restProps } = originalProps;
            return {
                ...restProps,
                // 不添加 onClick 事件，这样点击行的其他部分不会触发选中
            };
        },
        [getRowProps],
    );

    // 处理计划状态变更后的刷新
    const [originReloadToken, setOriginReloadToken] = useState(0);
    const handleStatusChange = useCallback(() => {
        if (canOfficialRead) {
            refetch();
        }
        setOriginReloadToken((current) => current + 1);
    }, [canOfficialRead, refetch]);

    // 查看计划详情
    const viewScheduleDetail = useCallback((schedule) => {
        setSelectedSchedule(schedule);
        setDetailDrawerVisible(true);
    }, []);

    // 复制为新草案
    const duplicateSchedule = useCallback(
        async (schedule) => {
            // 检查创建权限
            if (!hasPermission("schedule:create")) {
                message.error("您没有创建航班计划的权限");
                return;
            }

            try {
                message.loading("正在复制计划...", 0);
                const response = await scheduleService.duplicateSchedule(schedule.id);
                message.destroy();

                // 按照API规范检查响应格式
                if (response?.data?.success) {
                    message.success(response.data.message || "计划复制成功");
                    // 跳转到编辑器，使用返回的新计划ID
                    const newScheduleId = response.data.data?.id;
                    if (newScheduleId) {
                        navigate(`/flightSchedule/editor/${newScheduleId}`);
                    } else {
                        message.error("复制失败: 服务器返回的数据中缺少新计划ID");
                    }
                } else {
                    const errorMsg = response?.data?.message || "复制失败: 服务器返回无效数据";
                    message.error(errorMsg);
                }
            } catch (error) {
                message.destroy();
                // 优先使用后端返回的错误信息
                const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message || "未知错误";
                message.error(`复制失败: ${errorMsg}`);
            }
        },
        [hasPermission, navigate],
    );

    // 处理行点击事件
    const handleRowClick = useCallback(
        (record) => {
            viewScheduleDetail(record);
        },
        [viewScheduleDetail],
    );

    // 处理表格分页变化
    const handleTableChange = useCallback((newPagination, filters, sorter) => {
        setPagination(newPagination);
    }, []);

    // 处理搜索
    const handleSearch = useCallback(
        (values) => {
            // 处理搜索参数，确保与后端API参数匹配
            const params = {};

            if (values.name) {
                params.name = values.name;
            }

            // 修复日期参数名称，与后端API保持一致
            if (values.dateRange && values.dateRange.length === 2) {
                if (values.dateRange[0]) {
                    params.start_date = values.dateRange[0].format("YYYY-MM-DD");
                }
                if (values.dateRange[1]) {
                    params.end_date = values.dateRange[1].format("YYYY-MM-DD");
                }
            }

            // 处理飞机ID筛选 - 支持多选，转换为逗号分隔的字符串
            if (values.aircraft_id && values.aircraft_id.length > 0) {
                if (Array.isArray(values.aircraft_id)) {
                    params.aircraft_id = values.aircraft_id.join(",");
                } else {
                    params.aircraft_id = values.aircraft_id.toString(); // 单个值转为字符串
                }
            }

            // 处理状态筛选 - 修复取消选择后的逻辑
            if (values.status && values.status.length > 0) {
                if (Array.isArray(values.status)) {
                    params.status = values.status.join(",");
                } else {
                    params.status = values.status; // 已经是字符串或单个值
                }
            } else {
                // 如果没有选择任何状态，默认查询所有非草稿状态
                params.status = "SUBMITTED,CONFIRMATION_PENDING,AIRLINE_CONFIRMED,PUBLISHED,DEPRECATED";
            }

            setFilterParams(params);
            setPagination({
                ...pagination,
                current: 1, // 重置到第一页
            });
        },
        [pagination],
    );

    // 重置搜索
    const handleReset = useCallback(() => {
        setFilterParams({
            status: "SUBMITTED,CONFIRMATION_PENDING,AIRLINE_CONFIRMED,PUBLISHED,DEPRECATED",
        });
        setPagination({
            ...pagination,
            current: 1,
        });
    }, [pagination]);

    // 筛选摘要项
    const OFFICIAL_STATUS_LABELS = {
        SUBMITTED: "已提交", CONFIRMATION_PENDING: "待确认",
        AIRLINE_CONFIRMED: "航司确认", PUBLISHED: "已发布", DEPRECATED: "已废弃",
    };
    const DEFAULT_OFFICIAL_STATUSES = "SUBMITTED,CONFIRMATION_PENDING,AIRLINE_CONFIRMED,PUBLISHED,DEPRECATED";

    const filterSummaryItems = useMemo(() => {
        const items = [];
        // 状态——始终显示
        if (filterParams.status) {
            const statusArr = filterParams.status.split(",");
            const allLabels = statusArr.map((s) => OFFICIAL_STATUS_LABELS[s] || s);
            const fullValue = allLabels.join(" / ");
            const isDefault = filterParams.status === DEFAULT_OFFICIAL_STATUSES;
            const value = formatArraySummary(statusArr, OFFICIAL_STATUS_LABELS);
            items.push({
                label: "状态",
                value: isDefault ? `${statusArr.length}项 (默认)` : value,
                fullValue,
            });
        }
        if (filterParams.name) items.push({ label: "计划名称", value: filterParams.name });
        if (filterParams.aircraft_id) {
            const ids = filterParams.aircraft_id.split(",");
            items.push({ label: "飞机", value: `${ids.length}架` });
        }
        if (filterParams.start_date || filterParams.end_date) {
            items.push({ label: "日期", value: `${filterParams.start_date || "..."} ~ ${filterParams.end_date || "..."}` });
        }
        return items;
    }, [filterParams]);

    // 快捷搜索
    const quickSearch = useQuickSearch({ setFilterParams, setPagination });

    // 处理批量发送发布邮件
    const handleBatchSendPublishEmail = useCallback(() => {
        if (selectedRows.length === 0) {
            message.warning("请至少选择一个航班计划");
            return;
        }

        // 检查选中计划的状态是否允许发送发布邮件
        const invalidSchedules = selectedRows.filter((schedule) => !["AIRLINE_CONFIRMED"].includes(schedule.status));

        if (invalidSchedules.length > 0) {
            message.warning("只能对航司确认状态的计划发送发布邮件");
            return;
        }

        // 使用统一邮件发送抽屉
        setUnifiedEmailType("publish");
        setUnifiedEmailDrawerVisible(true);
    }, [selectedRows]);

    // 批量发布邮件发送成功后的处理
    const handleBatchPublishEmailSuccess = useCallback(() => {
        setBatchPublishEmailDrawerVisible(false);
        clearSelection();
        refetch();
        message.success("发布邮件发送成功");
    }, [clearSelection, refetch]);

    // 统一邮件发送成功后的处理
    const handleUnifiedEmailSuccess = useCallback(() => {
        setUnifiedEmailDrawerVisible(false);
        clearSelection();
        refetch();
        const successMessage = unifiedEmailType === "confirmation" ? "确认邮件发送成功" : "发布邮件发送成功";
        message.success(successMessage);
    }, [clearSelection, refetch, unifiedEmailType]);

    // 处理点击操作人显示活动记录
    const handleOperatorClick = (schedule) => {
        setSelectedScheduleForActivity(schedule);
        setActivityDrawerVisible(true);
    };

    // 关闭活动记录抽屉
    const handleCloseActivityDrawer = () => {
        setActivityDrawerVisible(false);
        setSelectedScheduleForActivity(null);
    };

    // 如果认证信息还在加载中，显示加载状态
    if (authLoading) {
        return (
            <div className="page-container">
                <Card className="data-card">
                    <div style={{ textAlign: "center", padding: "50px 0" }}>
                        <Spin size="large" />
                        <p style={{ marginTop: 16 }}>正在加载用户信息...</p>
                    </div>
                </Card>
            </div>
        );
    }

    if (!canIngestRead && !canOfficialRead) {
        return (
            <div className="page-container">
                <Card className="data-card">
                    <div style={{ textAlign: "center", padding: "50px 0" }}>
                        <h3>无权限访问</h3>
                        <p>您没有查看正式航班计划或航司班期接入的权限，请联系管理员。</p>
                        <p style={{ color: "#666", fontSize: "12px", marginTop: 16 }}>
                            当前用户: {user?.username || "未知"} | 权限列表: {user?.permissions?.length ? user.permissions.join(", ") : "无权限"}
                        </p>
                    </div>
                </Card>
            </div>
        );
    }

    // 表格列定义
    const columns = [
        getIndexColumn("序号", 60),
        {
            title: "计划名称",
            dataIndex: "name",
            key: "name",
            ellipsis: true,
            width: 180,
            render: (text, record) => (
                <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        viewScheduleDetail(record);
                    }}
                >
                    {text || "未命名计划"}
                </a>
            ),
        },
        {
            title: "时间范围",
            key: "date_range",
            width: 160,
            render: (_, record) => {
                if (!record.start_date && !record.end_date) return "无时间范围";
                const start = record.start_date ? formatDate(record.start_date) : "未设置";
                const end = record.end_date ? formatDate(record.end_date) : "未设置";
                return `${start} 至 ${end}`;
            },
        },
        {
            title: "飞机",
            key: "aircraft",
            width: 120,
            render: (_, record) => {
                if (!record.aircraft) return "未分配";
                const registration = record.aircraft.registration || "未知";
                return <AircraftTag registration={registration} />;
            },
        },
        {
            title: "航班数量",
            dataIndex: "flight_count",
            key: "flight_count",
            width: 100,
            render: (text) => text || 0,
        },
        {
            title: "状态",
            dataIndex: "status",
            key: "status",
            width: 120,
            render: (status) => <Badge className="status-badge" status={getStatusColor(status)} text={getStatusText(status)} />,
        },
        {
            title: "创建时间",
            dataIndex: "created_at",
            key: "created_at",
            width: 180,
            render: (text) => (text ? formatDateTime(text) : "未知"),
        },
        {
            title: "更新时间",
            dataIndex: "updated_at",
            key: "updated_at",
            width: 180,
            render: (text) => (text ? formatDateTime(text) : "未知"),
        },
        {
            title: "操作人",
            key: "last_operator",
            width: 120,
            render: (_, record) => {
                if (record.last_operator_name) {
                    const operatorTypeMap = {
                        USER: { color: "blue", text: "用户" },
                        SYSTEM: { color: "green", text: "系统" },
                        AI_AGENT: { color: "purple", text: "AI" },
                        API_CLIENT: { color: "orange", text: "API" },
                    };
                    const typeConfig = operatorTypeMap[record.last_operator_type] || { color: "default", text: "未知" };

                    return (
                        <Tooltip title={`${typeConfig.text}: ${record.last_operator_name} (点击查看活动记录)`}>
                            <Tag color={typeConfig.color} size="small" style={{ cursor: "pointer" }} onClick={() => handleOperatorClick(record)}>
                                {record.last_operator_name}
                            </Tag>
                        </Tooltip>
                    );
                }
                return <span style={{ color: "#999" }}>-</span>;
            },
        },
        {
            title: "操作",
            key: "action",
            fixed: "right",
            width: 220,
            render: (_, record) => {
                const duplicateItems = hasPermission("schedule:create")
                    ? [
                          {
                              key: "duplicate",
                              label: "复制为新草案",
                              icon: <CopyOutlined />,
                              onClick: () => duplicateSchedule(record),
                          },
                      ]
                    : [];
                return (
                    <Space size="small" className="table-actions" wrap={false}>
                        <PermissionGuard permission={["schedule:read", "schedule_official:read"]} requireAll={false}>
                            <Button
                                type="link"
                                size="small"
                                icon={<EyeOutlined />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    viewScheduleDetail(record);
                                }}
                            >
                                详情
                            </Button>
                        </PermissionGuard>
                        <PermissionGuard permission={["schedule:read", "schedule_official:read"]} requireAll={false}>
                            <ExportButton
                                resourceType="schedule"
                                resourceId={record.id}
                                buttonText="导出"
                                buttonProps={{
                                    type: "link",
                                    size: "small",
                                    icon: <ExportOutlined />,
                                    onClick: (e) => e.stopPropagation(),
                                }}
                            />
                        </PermissionGuard>
                        {hasIngestSource(record) ? (
                            <AirlineIngestSourceButtons variant="menu" scheduleId={record.id} extraItems={duplicateItems} />
                        ) : duplicateItems.length ? (
                            <Dropdown menu={{ items: duplicateItems }} placement="bottomRight" trigger={["click"]}>
                                <Button
                                    type="link"
                                    size="small"
                                    icon={<MoreOutlined />}
                                    aria-label="更多操作"
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </Dropdown>
                        ) : null}
                    </Space>
                );
            },
        },
    ];

    const ingestWorkspace = canIngestRead ? (
        <AirlineIngestOfficialWorkspace
            showPageTitle={!canOfficialRead}
            onOpenSchedule={viewScheduleDetail}
            onIngestConfirmed={canOfficialRead ? refetch : undefined}
            reloadToken={originReloadToken}
        />
    ) : null;

    const officialWorkspace = canOfficialRead ? (
        <>
            {!canIngestRead ? (
                <div className="page-header-compact">
                    <div className="page-title-compact">正式航班计划</div>
                </div>
            ) : null}
            <CollapsibleFilterBar
                summaryItems={filterSummaryItems}
                onReset={handleReset}
                drawerTitle="正式航班计划 - 筛选条件"
                quickControls={
                    <Input
                        placeholder="搜索名称/飞机注册号..."
                        style={{ width: 220, height: 26 }}
                        {...quickSearch.inputProps}
                    />
                }
            >
                <ScheduleOfficialFilter onSearch={handleSearch} onReset={handleReset} autoApply />
            </CollapsibleFilterBar>
            {selectedRowKeys.length > 0 && (
                <div className="batch-operations-info">
                    <span style={{ marginRight: 8, fontSize: "12px" }}>
                        已选择 <span style={{ fontWeight: "bold" }}>{selectedRowKeys.length}</span> 个计划
                    </span>
                    <Divider type="vertical" style={{ margin: "0 4px" }} />
                    <Button size="small" type="link" onClick={handleSelectAllVisible} style={{ padding: "0 4px" }}>
                        全选当前页
                    </Button>
                    <Divider type="vertical" style={{ margin: "0 4px" }} />
                    <Button size="small" type="link" onClick={clearSelection} style={{ padding: "0 4px" }}>
                        取消选择
                    </Button>
                    <Divider type="vertical" style={{ margin: "0 4px" }} />
                    <PermissionGuard permission="schedule_official:publish">
                        <Button size="small" type="primary" ghost icon={<MailOutlined />} onClick={handleBatchSendPublishEmail} style={{ fontSize: "12px" }}>
                            发送航班发布邮件
                        </Button>
                    </PermissionGuard>
                </div>
            )}
            <Card className="data-card">
                <Table
                    size="small"
                    columns={columns}
                    dataSource={filteredData}
                    rowKey="id"
                    pagination={{
                        ...pagination,
                        total: data?.total || 0,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total) => `共 ${total} 条记录`,
                    }}
                    loading={isLoading}
                    onChange={handleTableChange}
                    scroll={{ x: 1200 }}
                    rowSelection={
                        hasPermission("schedule_official:confirm")
                            ? {
                                  ...rowSelection,
                                  columnWidth: 6,
                              }
                            : null
                    }
                    ref={tableRef}
                    onRow={getCustomRowProps}
                />
            </Card>
        </>
    ) : null;

    const handleWorkspaceChange = (key) => {
        const next = new URLSearchParams(searchParams);
        if (key === INGEST_WORKSPACE) {
            next.set(OFFICIAL_PAGE_WORKSPACE_QUERY, INGEST_WORKSPACE);
        } else {
            next.delete(OFFICIAL_PAGE_WORKSPACE_QUERY);
        }
        next.delete("scheduleId");
        setSearchParams(next, { replace: true });
    };

    const showTabs = canIngestRead && canOfficialRead;

    return (
        <div className="page-container">
            {showTabs ? (
                <>
                    <div className="page-header-compact">
                        <div className="page-title-compact">正式航班计划</div>
                    </div>
                    <Tabs
                        activeKey={workspace}
                        onChange={handleWorkspaceChange}
                        items={[
                            { key: OFFICIAL_WORKSPACE, label: "正式计划", children: officialWorkspace },
                            { key: INGEST_WORKSPACE, label: "航司班期接入", children: ingestWorkspace },
                        ]}
                    />
                </>
            ) : workspace === INGEST_WORKSPACE ? (
                ingestWorkspace
            ) : (
                officialWorkspace
            )}

            <ScheduleDetailDrawer visible={detailDrawerVisible} onClose={() => setDetailDrawerVisible(false)} schedule={selectedSchedule} onStatusChange={handleStatusChange} />
            <UnifiedEmailSendDrawer visible={unifiedEmailDrawerVisible} onClose={() => setUnifiedEmailDrawerVisible(false)} schedules={selectedRows} emailType={unifiedEmailType} onSuccess={handleUnifiedEmailSuccess} />
            {batchPublishEmailDrawerVisible && <BatchPublishEmailSendDrawer visible={batchPublishEmailDrawerVisible} onClose={() => setBatchPublishEmailDrawerVisible(false)} schedules={selectedRows} onSuccess={handleBatchPublishEmailSuccess} />}
            <ScheduleActivityDrawer visible={activityDrawerVisible} onClose={handleCloseActivityDrawer} scheduleId={selectedScheduleForActivity?.id} scheduleName={selectedScheduleForActivity?.name} title="活动记录" />
        </div>
    );
};

export default ScheduleOfficialList;
